import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppLayout } from "./layouts/AppLayout";
import { AuthLayout } from "./layouts/AuthLayout";
import { TenantLayout } from "./layouts/TenantLayout";
import { OwnerRoute } from "@/components/auth/OwnerRoute";
import { OnboardingRoute } from "@/components/auth/OnboardingRoute";
import { OwnerLayout } from "./layouts/OwnerLayout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { TenantRoute } from "@/components/auth/TenantRoute";
import { useAuth } from "@/lib/auth";
import { useTenantIdentity } from "@/lib/useTenantIdentity";
import { useTenantAutoLink } from "@/lib/useTenantAutoLink";
import { useOwnerAutoLink } from "@/lib/useOwnerAutoLink";
import { useOwnerIdentity } from "@/lib/useOwnerIdentity";
import { LoadingScreen } from "@/components/shell/LoadingScreen";

const LoginPage = lazy(() => import("@/features/auth/LoginPage"));
const SignupPage = lazy(() => import("@/features/auth/SignupPage"));
const ForgotPasswordPage = lazy(() => import("@/features/auth/ForgotPasswordPage"));
const CreateOrganizationPage = lazy(
  () => import("@/features/onboarding/CreateOrganizationPage"),
);
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));
const PropertiesPage = lazy(() => import("@/features/properties/PropertiesPage"));
const PropertyDetailPage = lazy(
  () => import("@/features/properties/PropertyDetailPage"),
);
const UnitsPage = lazy(() => import("@/features/units/UnitsPage"));
const UnitDetailPage = lazy(() => import("@/features/units/UnitDetailPage"));
const TenantDetailPage = lazy(() => import("@/features/tenants/TenantDetailPage"));
const TenantsPage = lazy(() => import("@/features/tenants/TenantsPage"));
const LeasesPage = lazy(() => import("@/features/leases/LeasesPage"));
const LeaseNewPage = lazy(() => import("@/features/leases/LeaseNewPage"));
const LeaseReviewPage = lazy(() => import("@/features/leases/LeaseReviewPage"));
const LeaseDetailPage = lazy(() => import("@/features/leases/LeaseDetailPage"));
const LeaseEditPage = lazy(() => import("@/features/leases/LeaseEditPage"));
const OwnersPage = lazy(() => import("@/features/owners/OwnersPage"));
const OwnerStatementPage = lazy(() => import("@/features/owners/OwnerStatementPage"));
const ReportsPage = lazy(() => import("@/features/reports/ReportsPage"));
const TaxSummaryPage = lazy(() => import("@/features/reports/TaxSummaryPage"));
const ExpensesPage = lazy(() => import("@/features/finance/ExpensesPage"));
const RentPage = lazy(() => import("@/features/finance/RentPage"));
const BrainHistoryPage = lazy(() => import("@/features/brain/BrainHistoryPage"));
const MaintenanceListPage = lazy(() => import("@/features/maintenance/MaintenanceListPage"));
const MaintenanceNewPage = lazy(() => import("@/features/maintenance/MaintenanceNewPage"));
const MaintenanceDetailPage = lazy(() => import("@/features/maintenance/MaintenanceDetailPage"));
const OrganizationSettingsPage = lazy(
  () => import("@/features/settings/OrganizationSettingsPage"),
);
const TenantWelcomePage = lazy(() => import("@/features/tenants/TenantWelcomePage"));
const TenantHomePage = lazy(() => import("@/features/tenant-portal/TenantHomePage"));
const TenantMaintenancePage = lazy(
  () => import("@/features/tenant-portal/TenantMaintenancePage"),
);
const TenantMaintenanceNewPage = lazy(
  () => import("@/features/tenant-portal/TenantMaintenanceNewPage"),
);
const TenantNoticesPage = lazy(
  () => import("@/features/tenant-portal/TenantNoticesPage"),
);
const OwnerWelcomePage = lazy(() => import("@/features/owners/OwnerWelcomePage"));
const OwnerHomePage = lazy(() => import("@/features/owners/OwnerHomePage"));

export function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute requireOrg={false}>
              <OnboardingRoute>
                <CreateOrganizationPage />
              </OnboardingRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/app"
          element={
            <ProtectedRoute requireOrg={true}>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="properties/:id" element={<PropertyDetailPage />} />
          <Route path="units" element={<UnitsPage />} />
          <Route path="units/:id" element={<UnitDetailPage />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenants/:id" element={<TenantDetailPage />} />
          <Route path="leases" element={<LeasesPage />} />
          <Route path="leases/new" element={<LeaseNewPage />} />
          <Route path="leases/new/review" element={<LeaseReviewPage />} />
          <Route path="leases/:id" element={<LeaseDetailPage />} />
          <Route path="leases/:id/edit" element={<LeaseEditPage />} />
          <Route path="owners" element={<OwnersPage />} />
          <Route path="owners/:id" element={<OwnerStatementPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="reports/tax" element={<TaxSummaryPage />} />
          <Route path="finance" element={<ExpensesPage />} />
          <Route path="finance/rent" element={<RentPage />} />
          <Route path="brain" element={<BrainHistoryPage />} />
          <Route path="maintenance" element={<MaintenanceListPage />} />
          <Route path="maintenance/new" element={<MaintenanceNewPage />} />
          <Route path="maintenance/:id" element={<MaintenanceDetailPage />} />
          <Route
            path="settings/organization"
            element={<OrganizationSettingsPage />}
          />
        </Route>

        {/* Tenant portal (Phase 6.11). Separate user class, minimal layout. */}
        <Route
          path="/tenant/welcome"
          element={
            <TenantRoute requireLink={false}>
              <TenantWelcomePage />
            </TenantRoute>
          }
        />
        <Route
          path="/tenant"
          element={
            <TenantRoute requireLink={true}>
              <TenantLayout />
            </TenantRoute>
          }
        >
          <Route index element={<TenantHomePage />} />
          <Route path="notices" element={<TenantNoticesPage />} />
          <Route path="maintenance" element={<TenantMaintenancePage />} />
          <Route path="maintenance/new" element={<TenantMaintenanceNewPage />} />
        </Route>

        {/* Owner portal (Phase 6.12). */}
        <Route
          path="/owner/welcome"
          element={
            <OwnerRoute requireLink={false}>
              <OwnerWelcomePage />
            </OwnerRoute>
          }
        />
        <Route
          path="/owner"
          element={
            <OwnerRoute requireLink={true}>
              <OwnerLayout />
            </OwnerRoute>
          }
        >
          <Route index element={<OwnerHomePage />} />
        </Route><Route path="/" element={<RoleHome />} />
        <Route path="*" element={<RoleHome />} />
      </Routes>
    </Suspense>
  );
}

// Role-aware landing for "/" and unknown paths. Sends a linked tenant to their
// portal and everyone else to the manager app. Mount point for useTenantAutoLink
// (Phase 6.11.2 hardening): an authenticated, unlinked, non-manager user with a
// pending invite is linked here on first load, then routed to /tenant once
// identity refetches. While the auto-link attempt is in flight, the loading
// screen is held so the user never briefly lands on /app or /onboarding before
// the link completes.
function RoleHome() {
  const { user, loading: authLoading } = useAuth();
  const { inFlight: autoLinkInFlight } = useTenantAutoLink();
  const { inFlight: ownerAutoLinkInFlight } = useOwnerAutoLink();
  const {
    data: owner,
    isLoading: oLoading,
    isFetching: oFetching,
    isSuccess: oLoaded,
  } = useOwnerIdentity();

  const {
    data: tenant,
    isLoading: tLoading,
    isFetching: tFetching,
    isSuccess: tLoaded,
  } = useTenantIdentity();

  if (authLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (tLoading || tFetching || !tLoaded) return <LoadingScreen />;
  if (autoLinkInFlight) return <LoadingScreen />;
  if (oLoading || oFetching || !oLoaded) return <LoadingScreen />;
  if (ownerAutoLinkInFlight) return <LoadingScreen />;
  if (owner) return <Navigate to="/owner" replace />;
  if (tenant) return <Navigate to="/tenant" replace />;
  return <Navigate to="/app/dashboard" replace />;
}
